import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../../utils/api';
import useSwipe from '../../hooks/useSwipe';

/**
 * AdBanner — internal promotion slot ONLY.
 *
 *   No third-party ad networks. Ever.
 *
 * Feb 25, 2026 — cinematic redesign:
 *   - Larger immersive surface (not a tiny banner card).
 *   - Layered dark gradient overlays on top of the cover art.
 *   - Floating CTA pill (“Read Now” / “Continue”) anchored bottom-left.
 *   - Social-proof row (reads, votes, "Trending" pill).
 *   - Premium typography hierarchy: serif display title + sans body.
 *   - Soft outer glow + inner ring instead of hard borders.
 *   - Two-line teaser copy from `blurb`.
 *   - Mobile-first: stacks tall on small screens, wider on md+.
 *   - Preserves the rotating pool from `/api/promos/{placement}` so the
 *     analytics/impression contracts stay intact.
 *
 * `placement` is still mandatory; `size` is now an aesthetic intent
 * (cinematic | rectangle | leaderboard) — sizes map to responsive
 * Tailwind heights, not fixed pixel boxes.
 */

const SIZE_PRESETS = {
  // Hero-style — used on HomePage top & bottom
  cinematic: {
    heightClass: 'h-72 sm:h-80 md:h-96',
    maxWidthClass: 'w-full',
  },
  // Smaller in-flow card — used inside library/contest pages
  leaderboard: {
    heightClass: 'h-44 sm:h-52',
    maxWidthClass: 'w-full',
  },
  rectangle: {
    heightClass: 'h-64',
    maxWidthClass: 'w-full max-w-md mx-auto',
  },
  // Legacy keys fall back to cinematic so old call-sites still look
  // premium without a code change.
  'large-leaderboard': { heightClass: 'h-44 sm:h-52', maxWidthClass: 'w-full' },
  'large-rectangle':   { heightClass: 'h-72', maxWidthClass: 'w-full max-w-md mx-auto' },
  skyscraper:          { heightClass: 'h-[600px]', maxWidthClass: 'w-full max-w-[200px]' },
  auto:                { heightClass: 'h-72 sm:h-80 md:h-96', maxWidthClass: 'w-full' },
};

const ROTATION_MS = 12000;
const FADE_MS = 350;

const formatCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(v);
};

const ctaLabel = (kind) => {
  switch (kind) {
    case 'active_campaign': return 'Read Now';
    case 'exclusive_contracted': return 'Open Exclusive';
    case 'trending_promoted':
    case 'trending_fallback':
    default: return 'Read Now';
  }
};

const AdBanner = ({ placement, size = 'cinematic', className = '' }) => {
  const navigate = useNavigate();
  const [pool, setPool] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.cinematic;

  // --- One-shot fetch (no polling) ---------------------------------------
  // NOTE: We deliberately do NOT gate with a useRef "fetchedRef" here.
  // React.StrictMode runs effects twice in dev; a ref gate combined with
  // an `alive` cleanup flag silently drops the first mount's response
  // (cleanup flips alive=false BEFORE the fetch resolves) and the second
  // mount is gated out — leaving the banner empty. The `alive` closure
  // alone is sufficient: each effect has its own flag, stale responses
  // are ignored, and the live one always succeeds.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiCall('get', `/promos/${encodeURIComponent(placement)}`);
        const items = Array.isArray(r?.data?.items) ? r.data.items : [];
        if (!alive) return;
        if (items.length > 0) { setPool(items); return; }
        // Pool empty → preserve the legacy single-story fallback.
        try {
          const t = await apiCall('get', '/stories/trending');
          if (!alive) return;
          if (Array.isArray(t?.data) && t.data.length > 0) {
            const s = t.data[0];
            setPool([{
              id: s.id,
              title: s.title,
              cover_image: s.cover || s.cover_image,
              cta_url: `/story/${s.id}`,
              kind: 'trending_fallback',
              blurb: s.blurb || '',
              tropes: (s.tropes || []).slice(0, 3),
              views: s.views || 0,
              likes_count: s.likes_count || 0,
              votes_count: s.votes_count || 0,
              is_promoted: !!s.is_promoted,
              author_name: s.author_name,
            }]);
          }
        } catch { /* leave empty */ }
      } catch { /* leave empty */ }
    })();
    return () => { alive = false; };
  }, [placement]);

  // --- Rotation + swipe + press-hold pause (Feb 25, 2026) ----------------
  // The carousel auto-rotates every ROTATION_MS but the user can pause
  // by pressing-and-holding the card (Stories-style). We track the
  // elapsed time per cycle so resume continues from where we left off
  // instead of restarting at 0% — that's what gives the carousel its
  // premium "alive" feel.
  const [rotationKey, setRotationKey] = useState(0);
  const progressRef = useRef(null);
  const timerRef = useRef(null);
  const cycleStartRef = useRef(0);          // ms timestamp of current cycle start
  const remainingRef = useRef(ROTATION_MS); // ms left before next slide
  const pausedRef = useRef(false);

  const advance = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setIdx((i) => (i + 1) % pool.length);
      setVisible(true);
      setRotationKey((k) => k + 1); // triggers a fresh cycle
    }, FADE_MS);
  }, [pool.length]);

  const startProgress = useCallback((fromPct, durationMs) => {
    const el = progressRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.width = `${fromPct}%`;
     
    void el.offsetWidth; // force reflow so the next transition animates
    el.style.transition = `width ${durationMs}ms linear`;
    el.style.width = '100%';
  }, []);

  const freezeProgress = useCallback(() => {
    const el = progressRef.current;
    if (!el) return;
    const computed = getComputedStyle(el).width;       // px value
    el.style.transition = 'none';
    el.style.width = computed;
  }, []);

  const goTo = useCallback((nextIdx) => {
    if (pool.length <= 1) return;
    setVisible(false);
    setTimeout(() => {
      setIdx(((nextIdx % pool.length) + pool.length) % pool.length);
      setVisible(true);
    }, FADE_MS);
    setRotationKey((k) => k + 1); // restart full cycle on manual nav
  }, [pool.length]);

  const pauseRotation = useCallback(() => {
    if (pausedRef.current || pool.length <= 1) return;
    pausedRef.current = true;
    clearTimeout(timerRef.current);
    const elapsed = Date.now() - cycleStartRef.current;
    remainingRef.current = Math.max(300, ROTATION_MS - elapsed);
    freezeProgress();
  }, [freezeProgress, pool.length]);

  const resumeRotation = useCallback(() => {
    if (!pausedRef.current || pool.length <= 1) return;
    pausedRef.current = false;
    const dur = remainingRef.current;
    cycleStartRef.current = Date.now() - (ROTATION_MS - dur);
    timerRef.current = setTimeout(advance, dur);
    // Resume the progress bar from its current visual width (already
    // frozen on pause) and animate to 100% over the remaining time.
    const el = progressRef.current;
    if (el) {
      const trackWidth = el.parentElement?.offsetWidth || 1;
      const currentPx = parseFloat(getComputedStyle(el).width) || 0;
      const currentPct = (currentPx / trackWidth) * 100;
      startProgress(currentPct, dur);
    }
  }, [advance, startProgress, pool.length]);

  const swipeHandlers = useSwipe({
    onSwipeLeft: () => goTo(idx + 1),
    onSwipeRight: () => goTo(idx - 1),
    onPressStart: pauseRotation,
    onPressEnd: resumeRotation,
  });

  // Drive the auto-rotation cycle. Re-runs whenever the user manually
  // navigates (rotationKey++) so each new slide gets a full ROTATION_MS.
  useEffect(() => {
    if (pool.length <= 1) return undefined;
    pausedRef.current = false;
    cycleStartRef.current = Date.now();
    remainingRef.current = ROTATION_MS;
    timerRef.current = setTimeout(advance, ROTATION_MS);
    startProgress(0, ROTATION_MS);
    return () => clearTimeout(timerRef.current);
  }, [pool, rotationKey, advance, startProgress]);

  // --- Preload the next image so swap is flicker-free --------------------
  useEffect(() => {
    if (pool.length <= 1) return;
    const next = pool[(idx + 1) % pool.length];
    if (next?.cover_image) {
      const img = new Image();
      img.src = next.cover_image;
    }
  }, [idx, pool]);

  const current = pool[idx];

  // Reserved-height empty placeholder — keeps layout stable.
  if (!current) {
    return (
      <div
        className={`${preset.heightClass} ${preset.maxWidthClass} rounded-2xl ${className}`}
        data-testid={`ad-banner-${placement}`}
      />
    );
  }

  const onActivate = () => {
    // Best-effort click analytics — only for billable campaign items.
    if (current.kind === 'active_campaign' && current.promotion_id) {
      apiCall('post', `/promos/${encodeURIComponent(placement)}/click`, {
        promotion_id: current.promotion_id,
        story_id: current.id,
      }).catch(() => {});
    }
    if (current.cta_url) {
      if (/^https?:\/\//i.test(current.cta_url)) {
        window.open(current.cta_url, '_blank', 'noopener');
      } else {
        navigate(current.cta_url);
      }
    }
  };

  // "Trending" pill — shows when admin has explicitly promoted OR when
  // this slot is filled by the trending-pool kinds.
  const showTrending =
    current.is_promoted ||
    current.kind === 'trending_promoted' ||
    current.kind === 'trending_fallback';
  const showExclusive = current.kind === 'exclusive_contracted';

  const reads = formatCount(current.views);
  const votes = formatCount(current.votes_count);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}
      {...swipeHandlers}
      className={`group relative cursor-pointer overflow-hidden rounded-2xl ${preset.heightClass} ${preset.maxWidthClass} ${className} ring-1 ring-white/5 shadow-[0_18px_50px_-20px_rgba(168,85,247,0.45)] hover:shadow-[0_24px_60px_-18px_rgba(236,72,153,0.55)] transition-shadow duration-500 touch-pan-y select-none`}
      style={{ backgroundColor: '#0b0613' }}
      data-testid={`ad-banner-${placement}`}
      data-promo-id={current.id}
      data-promo-kind={current.kind}
    >
      {/* Cover image */}
      {current.cover_image ? (
        <img
          src={current.cover_image}
          alt={current.title || 'Featured'}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-all duration-500 ${
            visible ? 'opacity-90 scale-100' : 'opacity-0 scale-105'
          } group-hover:scale-[1.03]`}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-purple-700/30 to-pink-700/30" />
      )}

      {/* Layered dark gradients — bottom-up legibility wash + side vignette */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

      {/* Top-right meta chips */}
      <div className={`absolute top-3 right-3 flex flex-wrap gap-1.5 justify-end transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        {showTrending && (
          <span
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-gradient-to-r from-pink-500/90 to-rose-500/90 text-white backdrop-blur-sm"
            data-testid="promo-trending-pill"
          >
            ★ Trending
          </span>
        )}
        {showExclusive && (
          <span
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-gradient-to-r from-amber-400/90 to-orange-500/90 text-black backdrop-blur-sm"
            data-testid="promo-exclusive-pill"
          >
            ✦ Exclusive
          </span>
        )}
        {!showTrending && !showExclusive && (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-white/10 text-white/80 backdrop-blur-sm">
            Featured
          </span>
        )}
      </div>

      {/* Bottom content stack */}
      <div className={`absolute inset-x-0 bottom-0 p-4 sm:p-6 md:p-7 ${pool.length > 1 ? 'pb-8 sm:pb-10' : ''} transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <p className="text-[11px] uppercase tracking-[0.18em] text-pink-300/90 mb-2 font-medium">
          Storyfyc Picks
        </p>
        <h3
          className="text-white font-bold leading-tight tracking-tight text-xl sm:text-2xl md:text-3xl line-clamp-2 mb-2"
          style={{ fontFamily: 'Georgia, "Iowan Old Style", "Times New Roman", serif' }}
          data-testid="promo-title"
        >
          {current.title || 'Discover something new'}
        </h3>
        {current.blurb && (
          <p className="text-white/75 text-sm sm:text-base line-clamp-2 max-w-2xl mb-3" data-testid="promo-teaser">
            {current.blurb}
          </p>
        )}

        {/* Social-proof row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-white/70 text-xs sm:text-sm mb-4" data-testid="promo-social-proof">
          {current.views > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>👁</span>
              <span><strong className="text-white font-semibold">{reads}</strong> reads</span>
            </span>
          )}
          {current.votes_count > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>★</span>
              <span><strong className="text-white font-semibold">{votes}</strong> votes</span>
            </span>
          )}
          {(current.tropes || []).slice(0, 2).map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-[11px]">
              {t}
            </span>
          ))}
        </div>

        {/* Floating CTA pill */}
        <div className="flex items-center justify-between gap-3">
          <span
            className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold shadow-[0_8px_30px_-8px_rgba(255,255,255,0.6)] group-hover:bg-pink-100 transition-colors"
            data-testid="promo-cta"
          >
            <span>{ctaLabel(current.kind)}</span>
            <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-0.5">→</span>
          </span>
          {current.author_name && (
            <span className="hidden sm:inline text-white/55 text-xs italic">
              by {current.author_name}
            </span>
          )}
        </div>
      </div>

      {/* Pagination dots — bottom-center, only when there's more than one. */}
      {pool.length > 1 && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10"
          data-testid="promo-pagination-dots"
          onClick={(e) => e.stopPropagation()}
        >
          {pool.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
              }`}
              data-testid={`promo-dot-${i}`}
            />
          ))}
        </div>
      )}

      {/* Thin progress bar — sits flush with the bottom edge so it reads
          as a "time remaining" line, Netflix/ReelShort style. */}
      {pool.length > 1 && (
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 z-10 pointer-events-none"
          data-testid="promo-progress-track"
        >
          <div
            ref={progressRef}
            className="h-full bg-gradient-to-r from-pink-400 to-purple-400"
            style={{ width: '0%' }}
          />
        </div>
      )}

      {/* Inner soft glow on hover */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/0 group-hover:ring-white/10 transition-all duration-500" />
    </div>
  );
};

export default AdBanner;
